import { useState } from 'react';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import { useCancelToken } from '@/hooks/useCancelToken';
import { bffApiErrorHandler } from '@/shared/errorHandler';
import { BffApiError, FrontendErrorCode } from '@/models/bff-api-error';
import { isBffResponse } from '@/utils/type-guards';

// Types for API method and arguments
type BffApiMethodArgs<P = any, PP = any, C = any> = {
  payload?: P;
  pathParams?: PP;
  config?: C;
};

type BffApiMethod<P = any, PP = any, C = any, R = any> = (
  args: BffApiMethodArgs<P, PP, C>,
) => Promise<AxiosResponse<{ data: R }>>;

// Return type includes all generics
interface BffRequestReturn<P, PP, C, R> {
  loading: boolean;
  success: boolean;
  error: BffApiError | null;
  data: R | null;
  request: (args: {
    payload?: P;
    pathParams?: PP;
    axiosConfig?: C;
    successCallback?: (data: R | null) => void;
    errorCallback?: (error: BffApiError, fullError: unknown) => void;
  }) => void;
}

export function useBffRequest<
  P = unknown,
  PP = unknown,
  C extends AxiosRequestConfig = AxiosRequestConfig,
  R = unknown,
>(apiMethod: BffApiMethod<P, PP, C, R>): BffRequestReturn<P, PP, C, R> {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [data, setData] = useState<R | null>(null);
  const [error, setError] = useState<BffApiError | null>(null);
  const { newCancelToken } = useCancelToken();

  const request = (args: {
    payload?: P;
    pathParams?: PP;
    axiosConfig?: C;
    successCallback?: (data: R | null) => void;
    errorCallback?: (error: BffApiError, fullError: unknown) => void;
  }) => {
    setLoading(true);
    setSuccess(false);
    setError(null);

    const { payload, pathParams, axiosConfig, successCallback, errorCallback } = args;

    const config = {
      ...axiosConfig,
      cancelToken: newCancelToken(),
    } as C;

    apiMethod({ payload, pathParams, config })
      .then(response => {
        setLoading(false);
        if (response.status === 204) {
          setSuccess(true);
          setData(null);
          successCallback?.(null);
          return;
        }

        if (isBffResponse(response.data)) {
          setSuccess(true);
          setData(response.data.data);
          successCallback?.(response.data.data);
        } else {
          const unexpectedFormatError = { code: FrontendErrorCode.UnexpectedResponseFormat };
          setError(unexpectedFormatError);
          errorCallback?.(unexpectedFormatError, null);
        }
      })
      .catch(err => {
        setLoading(false);
        const handled = bffApiErrorHandler(err);
        setError(handled);
        errorCallback?.(handled, err);
      });
  };

  return { loading, success, error, data, request };
}
